// Copyright (c) 2024. Sendanor <info@sendanor.fi>. All rights reserved.

import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { LogService } from "../../../../../../io/hyperify/core/LogService";
import {
    SEOProps,
} from "./SEO";
import { SEOFragment } from "./SEOFragment";

const LOG = LogService.createLogger( 'SEODiv' );

export interface SEODivProps extends SEOProps {

    /**
     * The route path
     */
    readonly path : string;
    readonly className ?: string;
    readonly children : ReactNode;

}

export function SEODiv ( props: SEODivProps) {
    const className = props.className;
    const path = props.path;
    const divRef = useRef<HTMLDivElement>(null);
    const [observerThreshold, setObserverThreshold] = useState<number>(0.5);

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && window.location.pathname !== path) {
                    LOG.debug(`${path}: Changing history from scrolling: `, window.location.pathname, entry);
                    window.history.replaceState(null, "", path);
                } else {
                    LOG.debug(`${path}: NOT changing history from scrolling: `, window.location.pathname, entry);
                }
            });
        },
        [
            path,
        ]
    );

    const observer = useMemo<IntersectionObserver | null>(
        () : IntersectionObserver | null => typeof IntersectionObserver !== "undefined"
            ? new IntersectionObserver(handleIntersection, { threshold: observerThreshold })
            : null
        ,
        [
        handleIntersection,
        observerThreshold,
    ]);

    // Function to update the heights
    const updateThreshold = useCallback(
        () => {

            const minThreshold : number = 0.00;
            const maxThreshold : number = 0.75;
            let newThreshold : number = maxThreshold;
            if (divRef.current) {
                const innerHeight : number = window.innerHeight;
                const height : number = divRef.current.getBoundingClientRect().height;
                LOG.debug(`${path}: innerHeight = `, innerHeight);
                LOG.debug(`${path}: height = `, height);

                if (height <= innerHeight) {
                    newThreshold = maxThreshold;
                } else {
                    newThreshold = (Math.ceil(innerHeight / height * 100) / 100) / 2;
                    if (newThreshold > maxThreshold) {
                        newThreshold = maxThreshold;
                    }
                    if (newThreshold < minThreshold) {
                        newThreshold = minThreshold;
                    }
                }

            }

            if (newThreshold !== observerThreshold) {
                LOG.debug(`${path}: newThreshold = `, newThreshold);
                setObserverThreshold(newThreshold)
            }

        }, [
            observerThreshold,
            setObserverThreshold,
            path,
        ],
    );

    useEffect(() => {

        // Update heights on component mount
        updateThreshold();

        // Update heights on window resize
        window.addEventListener('resize', updateThreshold);

        // Clean up event listener on component unmount
        return () => {
            window.removeEventListener('resize', updateThreshold);
        };

    }, [
        updateThreshold,
    ]);

    useEffect(() => {
        const currentDivRef = divRef.current;
        if (observer && currentDivRef) {
            observer.observe(currentDivRef);
        }
        return () : void => {
            if (observer && currentDivRef) {
                observer.unobserve(currentDivRef);
            }
        };
    }, [
        observer,
    ]);

    const divProps = className ? {className}: {};
    // const seo = useSEOProps(props);
    return (
        <div ref={ divRef } {...divProps}>
            {/*<img src={seo.pageImageUrl} alt={""} />*/}
            <SEOFragment { ...props } />
        </div>
    );
}
